// @ts-check
/* eslint-disable @typescript-eslint/no-explicit-any */
// Rationale: This file is plain JS but enforced under TypeScript's checkJs=true.
// To keep this refactor minimal and behavior-safe, we temporarily disable TS checks here.
// Follow-up task can migrate this module to full JSDoc types or TS.
// cacheManager.js
// Centralized persistent data cache for lib/services and dataService
// Purpose: Provide a foundational cache layer.
// Note: This mirrors the cache logic previously embedded in dataService.js
//       All code paths should import from this module going forward.
// Logging: Uses centralized logger for consistent, configurable output.

import logger from '../utils/logger';
import { CACHE_VERSION } from '../constants/cacheVersion';

/*
  This module implements:
  - PersistentCache (localStorage-backed)
  - CACHE_CONFIG (permanent cache + incremental sync failure policy)
  - cacheManager API used by services and dataService

  It intentionally has NO imports from other app layers to preserve module boundaries.
*/

/**
 * PersistentCache - LocalStorage-backed storage with safe JSON helpers
 */
class PersistentCache {
  constructor() {
    this.prefix = 'timegenie_cache_';
    this.loadingStates = new Map();
    this.errorStates = new Map();
  }
  /** @param {string} dataType @param {string} userId */
  _getCacheKey(dataType, userId) {
    return `${this.prefix}${userId}_${dataType}`;
  }
  /**
   * @param {string} key
   */
  _safeGetItem(key) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      logger.warn(`Failed to parse cache item ${key}:`, error);
      localStorage.removeItem(key);
      return null;
    }
  }
  /**
   * @param {string} key
   * @param {any} value
   */
  _safeSetItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      logger.warn(`Failed to set cache item ${key}:`, error);
      this._clearOldestEntries();
      return false;
    }
  }
  _clearOldestEntries() {
    try {
      const cacheKeys = Object.keys(localStorage)
        .filter((key) => key.startsWith(this.prefix))
        .map((key) => {
          const data = this._safeGetItem(key);
          return { key, timestamp: data?.lastFetch || 0 };
        })
        .sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = Math.ceil(cacheKeys.length * 0.25);
      for (let i = 0; i < toRemove; i++) {
        const entry = cacheKeys[i];
        if (entry) localStorage.removeItem(entry.key);
      }
      logger.info(`🧹 Cleared ${toRemove} oldest cache entries due to storage quota`);
    } catch (error) {
      logger.warn('Failed to clear old cache entries:', error);
    }
  }
  /**
   * @param {string} dataType
   * @param {string} userId
   */
  _getCacheEntry(dataType, userId) {
    const key = this._getCacheKey(dataType, userId);
    return this._safeGetItem(key);
  }
  /**
   * @param {string} dataType
   * @param {string} userId
   * @param {any} entry
   */
  _setCacheEntry(dataType, userId, entry) {
    const key = this._getCacheKey(dataType, userId);
    this._safeSetItem(key, entry);
  }
}

// Export a singleton persistent cache instance
export const persistentCache = new PersistentCache();

/** Cache configuration */
export const CACHE_CONFIG = {
  TTL: 5 * 60 * 1000,
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  PERMANENT_CACHE: true,
  MAX_INCREMENTAL_FAILURES: 3,
  // Cooldown to avoid hammering full fetch on repeated failures (in ms)
  FULL_FETCH_COOLDOWN_MS: 30 * 1000,
};

/**
 * Normalize cache entries against the global CACHE_VERSION.
 * Entries written before the current version are treated as invalid and purged.
 * @param {string} cacheKey
 * @param {string} userId
 * @param {any} entry
 * @returns {any|null}
 */
function normalizeVersion(cacheKey, userId, entry) {
  if (!entry) return null;
  if (entry.version === CACHE_VERSION) return entry;
  try {
    const key = persistentCache._getCacheKey(cacheKey, userId);
    localStorage.removeItem(key);
  } catch (_) {
    // ignore storage errors; mismatch already invalidates entry
  }
  logger.info(
    `📦 Cache version mismatch for ${cacheKey} (user: ${userId || 'unknown'}) → invalidating`,
  );
  return null;
}

/**
 * cacheManager - The primary cache API used by services and dataService
 * All methods are documented inline for clarity.
 */
export const cacheManager = {
  /**
   * Check if cache exists and has data (for permanent cache strategy)
   * @param {string} cacheKey
   * @param {string} userId
   * @returns {boolean}
   */
  hasData(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    if (!cache) return false;
    return Array.isArray(cache.data) && cache.data.length > 0;
  },
  /** @param {string} cacheKey @param {string} userId */
  isValid(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    if (!cache || !cache.data || !cache.lastFetch || cache.userId !== userId) return false;
    if (CACHE_CONFIG.PERMANENT_CACHE) return true;
    const now = Date.now();
    const age = now - cache.lastFetch;
    return age < CACHE_CONFIG.TTL;
  },
  /** @param {string} cacheKey @param {string} userId */
  get(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    if (cache && this.isValid(cacheKey, userId)) {
      logger.debug(`📦 Cache HIT for ${cacheKey} (user: ${userId})`);
      return cache?.data ?? null;
    }
    logger.debug(`📦 Cache MISS for ${cacheKey} (user: ${userId})`);
    return null;
  },
  /** @param {string} cacheKey @param {string} userId */
  getAtomic(cacheKey, userId) {
    const now = Date.now();
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    if (!cache || !cache.data || !cache.lastFetch || cache.userId !== userId) {
      return { data: null, isValid: false, timestamp: now };
    }
    if (CACHE_CONFIG.PERMANENT_CACHE) {
      logger.debug(`📦 Atomic Cache HIT (permanent) for ${cacheKey} (user: ${userId})`);
      return { data: cache.data, isValid: true, timestamp: now };
    }
    const age = now - cache.lastFetch;
    const isValid = age < CACHE_CONFIG.TTL;
    if (isValid) {
      logger.debug(`📦 Atomic Cache HIT for ${cacheKey} (user: ${userId}, age: ${age}ms)`);
      return { data: cache.data, isValid: true, timestamp: now };
    }
    logger.info(`📦 Atomic Cache EXPIRED for ${cacheKey} (user: ${userId}, age: ${age}ms)`);
    return { data: null, isValid: false, timestamp: now };
  },
  /**
   * @param {string[]} cacheKeys
   * @param {string} userId
   */
  checkMultipleAtomic(cacheKeys, userId) {
    const now = Date.now();
    /** @type {Record<string, any>} */
    const results = {};
    let allValid = true;
    for (const cacheKey of cacheKeys) {
      const atomicResult = this.getAtomic(cacheKey, userId);
      results[cacheKey] = atomicResult;
      if (!atomicResult.isValid) allValid = false;
    }
    logger.info(
      `📦 Atomic Multi-Check: ${allValid ? 'ALL VALID' : 'SOME INVALID'} for keys [${cacheKeys.join(', ')}] (user: ${userId})`,
    );
    return { allValid, results, timestamp: now };
  },
  /**
   * Store data in cache with timestamp
   * @param {string} cacheKey
   * @param {any} data
   * @param {string} userId
   */
  set(cacheKey, data, userId) {
    logger.info(
      `📦 Cache SET for ${cacheKey} (user: ${userId}, items: ${Array.isArray(data) ? data.length : 'N/A'})`,
    );
    const cacheEntry = { data, lastFetch: Date.now(), userId, version: CACHE_VERSION };
    persistentCache._setCacheEntry(cacheKey, userId, cacheEntry);
    persistentCache.loadingStates.delete(`${userId}_${cacheKey}`);
    persistentCache.errorStates.delete(`${userId}_${cacheKey}`);
  },
  /**
   * Mark the start of a full fetch attempt. Records attempt timestamp for cooldown logic.
   * @param {string} cacheKey
   * @param {string} userId
   */
  markFullFetchAttempt(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    ) || { data: [], userId, version: CACHE_VERSION };
    cache.lastFullFetchAttemptAt = Date.now();
    persistentCache._setCacheEntry(cacheKey, userId, cache);
    logger.info(`🕒 Cache: Mark full fetch attempt for ${cacheKey} (user: ${userId})`);
  },
  /**
   * Mark a full fetch failure and increment failure counter.
   * @param {string} cacheKey
   * @param {string} userId
   */
  markFullFetchFailure(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    ) || { data: [], userId, version: CACHE_VERSION };
    cache.lastFullFetchErrorAt = Date.now();
    cache.fullFetchFailures = (cache.fullFetchFailures || 0) + 1;
    persistentCache._setCacheEntry(cacheKey, userId, cache);
    logger.warn(
      `⚠️ Cache: Full fetch failure #${cache.fullFetchFailures} for ${cacheKey} (user: ${userId})`,
    );
  },
  /**
   * Mark a successful full fetch and reset related counters.
   * @param {string} cacheKey
   * @param {string} userId
   */
  markFullFetchSuccess(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    ) || { data: [], userId, version: CACHE_VERSION };
    cache.fullFetchFailures = 0;
    cache.lastFullFetchErrorAt = undefined;
    cache.lastFullFetchAttemptAt = undefined;
    // Note: mergeIncremental sets lastSync and resets incrementalFailures; we just clear failure metadata here.
    persistentCache._setCacheEntry(cacheKey, userId, cache);
    logger.info(`✅ Cache: Full fetch success marked for ${cacheKey} (user: ${userId})`);
  },
  /**
   * Check if full fetch is within cooldown window after a recent failure or attempt.
   * @param {string} cacheKey
   * @param {string} userId
   * @returns {{active:boolean, reason:string, sinceMs:number}}
   */
  isFullFetchCooldownActive(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    const now = Date.now();
    if (!cache) return { active: false, reason: 'no-cache', sinceMs: 0 };
    const lastErr = cache.lastFullFetchErrorAt || 0;
    const lastAttempt = cache.lastFullFetchAttemptAt || 0;
    const sinceErr = now - lastErr;
    const sinceAttempt = now - lastAttempt;
    const withinErrCooldown = lastErr > 0 && sinceErr < CACHE_CONFIG.FULL_FETCH_COOLDOWN_MS;
    const withinAttemptCooldown =
      lastAttempt > 0 && sinceAttempt < CACHE_CONFIG.FULL_FETCH_COOLDOWN_MS / 2; // lighter cooldown for attempts
    if (withinErrCooldown) {
      return { active: true, reason: 'recent-error', sinceMs: sinceErr };
    }
    if (withinAttemptCooldown) {
      return { active: true, reason: 'recent-attempt', sinceMs: sinceAttempt };
    }
    return { active: false, reason: 'none', sinceMs: Math.min(sinceErr, sinceAttempt) };
  },
  /**
   * Set loading state for a cache key
   * @param {string} cacheKey
   * @param {boolean} loading
   * @param {string} userId
   */
  setLoading(cacheKey, loading, userId) {
    const stateKey = `${userId}_${cacheKey}`;
    if (loading) persistentCache.loadingStates.set(stateKey, true);
    else persistentCache.loadingStates.delete(stateKey);
  },
  /**
   * Get loading state for a cache key
   * @param {string} cacheKey
   * @param {string} userId
   */
  isLoading(cacheKey, userId) {
    const stateKey = `${userId}_${cacheKey}`;
    return persistentCache.loadingStates.has(stateKey);
  },
  /**
   * Set error state for a cache key
   * @param {string} cacheKey
   * @param {any} error
   * @param {string} userId
   */
  setError(cacheKey, error, userId) {
    const stateKey = `${userId}_${cacheKey}`;
    if (error) persistentCache.errorStates.set(stateKey, error);
    else persistentCache.errorStates.delete(stateKey);
    persistentCache.loadingStates.delete(stateKey);
  },
  /**
   * Get error state for a cache key
   * @param {string} cacheKey
   * @param {string} userId
   * @returns {Error|null}
   */
  getError(cacheKey, userId) {
    const stateKey = `${userId}_${cacheKey}`;
    return persistentCache.errorStates.get(stateKey) || null;
  },
  /**
   * Clear cache for a specific key or all keys for a user
   * @param {string} userId
   * @param {string|null} [cacheKey=null]
   */
  clear(userId, cacheKey = null) {
    if (cacheKey) {
      logger.info(`📦 Cache CLEAR for ${cacheKey} (user: ${userId})`);
      const key = persistentCache._getCacheKey(cacheKey, userId);
      localStorage.removeItem(key);
      const stateKey = `${userId}_${cacheKey}`;
      persistentCache.loadingStates.delete(stateKey);
      persistentCache.errorStates.delete(stateKey);
    } else {
      logger.info(`📦 Cache CLEAR ALL for user: ${userId}`);
      const userPrefix = `${persistentCache.prefix}${userId}_`;
      Object.keys(localStorage)
        .filter((key) => key.startsWith(userPrefix))
        .forEach((key) => localStorage.removeItem(key));
      const userStatePrefix = `${userId}_`;
      for (const stateKey of persistentCache.loadingStates.keys()) {
        if (stateKey.startsWith(userStatePrefix)) persistentCache.loadingStates.delete(stateKey);
      }
      for (const stateKey of persistentCache.errorStates.keys()) {
        if (stateKey.startsWith(userStatePrefix)) persistentCache.errorStates.delete(stateKey);
      }
    }
  },
  /**
   * Get cache statistics for debugging
   * @param {string} userId
   */
  getStats(userId) {
    const userPrefix = `${persistentCache.prefix}${userId}_`;
    const cacheKeys = Object.keys(localStorage).filter((key) => key.startsWith(userPrefix));
    const stats = {
      totalEntries: cacheKeys.length,
      totalSize: 0,
      entries: {},
      loadingStates: 0,
      errorStates: 0,
    };
    cacheKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      const dataType = key.replace(userPrefix, '');
      const entry = persistentCache._safeGetItem(key);
      const normalized = normalizeVersion(dataType, userId, entry);
      stats.totalSize += value ? value.length : 0;
      /** @type {any} */
      const entries = stats.entries;
      entries[dataType] = {
        size: value ? value.length : 0,
        itemCount: normalized?.data?.length || 0,
        lastFetch: normalized?.lastFetch ? new Date(normalized.lastFetch).toISOString() : null,
        age: normalized?.lastFetch ? Date.now() - normalized.lastFetch : null,
        isValid: normalized ? this.isValid(dataType, userId) : false,
        version: normalized?.version || null,
      };
    });
    const userStatePrefix = `${userId}_`;
    for (const stateKey of persistentCache.loadingStates.keys()) {
      if (stateKey.startsWith(userStatePrefix)) stats.loadingStates++;
    }
    for (const stateKey of persistentCache.errorStates.keys()) {
      if (stateKey.startsWith(userStatePrefix)) stats.errorStates++;
    }
    return stats;
  },
  /**
   * Get last successful incremental sync timestamp for a cache key
   * @param {string} cacheKey
   * @param {string} userId
   * @returns {string|null}
   */
  getLastSyncTimestamp(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    return cache?.lastSync || null;
  },
  /**
   * Merge new/updated items into cache and update sync timestamp
   * @param {string} cacheKey
   * @param {any[]} newItems
   * @param {string} userId
   * @param {string} syncTimestamp
   * @param {string} [idField='id']
   */
  mergeIncremental(cacheKey, newItems, userId, syncTimestamp, idField = 'id') {
    logger.info(`🔄 Cache: Merging ${newItems.length} new items into ${cacheKey}`);
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    ) || { data: [], version: CACHE_VERSION };
    const existingItems = cache.data || [];
    const existingMap = new Map(
      existingItems.map((/** @type {any} */ item) => [item[idField], item]),
    );
    let addedCount = 0;
    let updatedCount = 0;
    newItems.forEach((newItem) => {
      if (existingMap.has(newItem[idField])) {
        existingMap.set(newItem[idField], newItem);
        updatedCount++;
      } else {
        existingMap.set(newItem[idField], newItem);
        addedCount++;
      }
    });
    const mergedItems = Array.from(existingMap.values());
    const hasImportanceRank = mergedItems.some((item) => typeof item?.importance_rank === 'number');
    if (hasImportanceRank) {
      mergedItems.sort((a, b) => {
        const rankA =
          typeof a?.importance_rank === 'number' ? a.importance_rank : Number.POSITIVE_INFINITY;
        const rankB =
          typeof b?.importance_rank === 'number' ? b.importance_rank : Number.POSITIVE_INFINITY;
        if (rankA !== rankB) return rankA - rankB;
        const createdA = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const createdB = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return createdB - createdA;
      });
    } else if (mergedItems.length > 0 && mergedItems[0]?.created_at) {
      mergedItems.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    }
    const updatedCache = {
      data: mergedItems,
      lastFetch: Date.now(),
      lastSync: syncTimestamp,
      userId,
      incrementalFailures: 0,
      version: CACHE_VERSION,
    };
    persistentCache._setCacheEntry(cacheKey, userId, updatedCache);
    logger.info(
      `✅ Cache: Merged complete - ${addedCount} added, ${updatedCount} updated, ${mergedItems.length} total`,
    );
    return mergedItems;
  },
  /**
   * Record incremental sync failure and decide if full refresh is recommended
   * @param {string} cacheKey
   * @param {string} userId
   * @returns {boolean} true if full refresh is recommended
   */
  recordIncrementalFailure(cacheKey, userId) {
    const cache = normalizeVersion(
      cacheKey,
      userId,
      persistentCache._getCacheEntry(cacheKey, userId),
    );
    if (!cache) return true;
    const failures = (cache.incrementalFailures || 0) + 1;
    cache.incrementalFailures = failures;
    cache.version = CACHE_VERSION;
    persistentCache._setCacheEntry(cacheKey, userId, cache);
    logger.warn(`⚠️ Cache: Incremental sync failure #${failures} for ${cacheKey}`);
    if (failures >= CACHE_CONFIG.MAX_INCREMENTAL_FAILURES) {
      logger.warn(
        `🔄 Cache: Max incremental failures reached for ${cacheKey}, recommending full refresh`,
      );
      return true;
    }
    return false;
  },
  // Backward-compatibility helpers for legacy call patterns
  _getCurrentUserId() {
    try {
      const authKeys = Object.keys(localStorage).filter((key) => key.includes('auth-token'));
      if (authKeys.length > 0) {
        const raw = localStorage.getItem(/** @type {string} */ (authKeys[0]));
        if (raw) {
          const authData = JSON.parse(raw);
          if (authData?.user?.id) return authData.user.id;
        }
      }
    } catch (error) {
      logger.warn('Failed to get user ID from localStorage:', error);
    }
    if (typeof window !== 'undefined' && /** @type {any} */ (window).currentUser?.id) {
      return /** @type {any} */ (window).currentUser.id;
    }
    return null;
  },
  /** @param {string} cacheKey @param {any} loading @param {string} [userId] */
  setLoading_legacy(cacheKey, loading, userId) {
    if (!userId) {
      const uid = this._getCurrentUserId();
      if (!uid) {
        logger.warn(`⚠️ Cache setLoading called without userId for ${cacheKey}`);
        return;
      }
      userId = uid;
    }
    this.setLoading(cacheKey, loading, /** @type {string} */ (userId));
  },
  /** @param {string} cacheKey @param {any} error @param {string} [userId] */
  setError_legacy(cacheKey, error, userId) {
    if (!userId) {
      const uid = this._getCurrentUserId();
      if (!uid) {
        logger.warn(`⚠️ Cache setError called without userId for ${cacheKey}`);
        return;
      }
      userId = uid;
    }
    this.setError(cacheKey, error, /** @type {string} */ (userId));
  },
  /** @param {string} cacheKey @param {string} [userId] */
  clear_legacy(cacheKey, userId) {
    if (!userId) {
      const uid = this._getCurrentUserId();
      if (!uid) {
        logger.warn('⚠️ Cache clear called without userId');
        return;
      }
      userId = uid;
    }
    this.clear(/** @type {string} */ (userId), cacheKey);
  },
  /** @param {string} [userId] */
  debug(userId) {
    const uid = userId || this._getCurrentUserId() || '';
    if (uid) {
      logger.info('🔍 Cache Debug for user:', uid);
      logger.info('Cache stats:', this.getStats(uid));
    } else {
      logger.warn('⚠️ Cannot debug cache: no user ID available');
    }
  },
};

// Expose for browser debugging
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).cacheManager = cacheManager;
  /** @type {any} */ (window).persistentCache = persistentCache;
}

export default cacheManager;
